import { decamelizeKeys } from 'humps';
import { stripEmojis } from '../../lib/helpers';
import { request } from '../../actions/helper';
import { CHECKR_BFF_API_BASE } from '../../constants';
import {
  formatCustomPromptContent,
  collectPromptDocuments,
} from '../../components/Reports/Report/CandidateStories/helpers';

// ECDEX-3840 (slice 5): sharing a story with the account. This was the
// `submitCandidateStories` thunk; what it dispatched (processing, submitted,
// failed, the stories that came back) now lives with the hook that calls it
// (`useCandidateStories`).
export const buildCandidateStorySubmission = (values: any) => {
  const { candidateStories } = values;
  const candidate_stories: any[] = [];

  const formatDocuments = (docs: any[]) =>
    docs.map(doc => ({
      filename: doc.filename,
      tempfile: doc.url,
      type: doc.mimetype,
    }));

  // A selected item keeps its placeholder entry in candidateStories; the
  // toggles in CandidateStoryForm/Container.jsx delete it on deselect.
  const isSelected = (key: string) => Boolean(candidateStories[key]);

  const pushPromptDocumentsOnly = (key: string) => {
    const documents = collectPromptDocuments(key, candidateStories);

    if (documents.length > 0 && isSelected(key)) {
      candidate_stories.push({
        documents: formatDocuments(documents),
        record_id: key === 'general' ? undefined : key,
      });
    }
  };

  const hasSubmittedPromptResponses = candidateStories.promptGroup?.content;

  if (hasSubmittedPromptResponses) {
    const promptContentKeys = Object.keys(candidateStories.promptGroup.content);

    Object.entries(candidateStories.promptGroup.content).forEach(
      ([key, promptResponses]) => {
        const content = formatCustomPromptContent(
          promptResponses as Record<string, string>,
          values.promptGroups,
        );
        const documents = collectPromptDocuments(key, candidateStories);

        if (content) {
          const story: any = {
            content,
            documents: documents.length > 0 ? formatDocuments(documents) : [],
            record_id: key === 'general' ? undefined : key,
          };

          candidate_stories.push(story);
        } else {
          pushPromptDocumentsOnly(key);
        }
      },
    );

    // Items with prompt files but no prompt text entry at all
    Object.keys(candidateStories.promptGroup)
      .filter(key => key !== 'content' && !promptContentKeys.includes(key))
      .forEach(pushPromptDocumentsOnly);
  } else {
    const keys = Object.keys(candidateStories).filter(
      item => item !== 'content' && item !== 'promptGroup',
    );

    keys.forEach(key => {
      const story: any = {
        content: candidateStories.content?.[key],
        documents: [
          ...(candidateStories[key].documents || []),
          ...collectPromptDocuments(key, candidateStories),
        ],
        record_id: key === 'general' ? undefined : key,
      };

      story.content = story.content && stripEmojis(story.content);
      story.documents = story.documents && formatDocuments(story.documents);

      if (Object.keys(story.documents).length === 0) {
        story.documents = undefined;
      }

      candidate_stories.push(story);
    });
  }

  return {
    report: values.report,
    candidate_stories,
  };
};

// Resolves with the stories the BFF stored. `jwt` is the candidate session's;
// without one the applicant-portal token goes in the body instead.
export const submitCandidateStories = (
  values: any,
  jwt?: string | null,
  portalToken?: string | null,
): Promise<any> => {
  const submissionValues = buildCandidateStorySubmission(values);

  return request(`${CHECKR_BFF_API_BASE}/candidate_stories`, {
    method: 'POST',
    body: JSON.stringify({
      ...decamelizeKeys(submissionValues),
      ...(!jwt && portalToken ? { token: portalToken } : {}),
    }),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(jwt && { Authorization: `Bearer ${jwt}` }),
    },
  });
};
